/**
 * Compress a base64/data-URL image on the client side before uploading.
 * Prevents Cloudflare 520 on huge request bodies (multi-MB base64 payloads).
 *
 * @param {string} dataUrl        base64 string OR data-URL
 * @param {object} opts
 *   - maxWidth (default 1920)   downscale so image width <= maxWidth
 *   - quality  (default 0.82)   JPEG quality (0..1)
 *   - mimeType (default "image/jpeg")
 * @returns {Promise<string>}   compressed data-URL
 */
export async function compressImageDataUrl(dataUrl, opts = {}) {
  const maxWidth = opts.maxWidth || 1920;
  const quality = opts.quality ?? 0.82;
  const mime = opts.mimeType || "image/jpeg";

  if (!dataUrl || typeof dataUrl !== "string") return dataUrl;
  const src = dataUrl.startsWith("data:") ? dataUrl : `data:image/jpeg;base64,${dataUrl}`;

  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      try {
        const scale = img.width > maxWidth ? maxWidth / img.width : 1;
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL(mime, quality));
      } catch {
        resolve(dataUrl);
      }
    };
    img.onerror = () => resolve(dataUrl);
    img.src = src;
  });
}

// Approximate base64 payload size in bytes
export const b64Size = (s) => {
  if (!s) return 0;
  const raw = s.includes("base64,") ? s.split("base64,")[1] : s;
  return Math.ceil((raw.length * 3) / 4);
};
